import { CommonModule } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild, inject, input, model, signal } from '@angular/core';

import { Observable, map } from 'rxjs';

import { FilesDisplayComponent, FilesSlideComponent } from '@camelot/files-basic';
import { DocumentDto, FileType } from '@camelot/services';
import { EmptyComponent, LayoutFullPanelComponent } from '@camelot/ui';
import { CamBaseComponent, ModalState, extractEnum } from '@camelot/utils';

import { FileTypeCellComponent } from '../../grid/components/templates/file-type/file-type.component';
import { SizeCellComponent } from '../../grid/components/templates/size/size.component';
import {
  AgGridComponent,
  CamGridData,
  CamGridDataService,
  GridControlComponent,
  GridDateCellComponent,
  GridTagComponent,
  IFieldsOverrides,
} from '../../grid/public-api';

export interface DocumentEntity {
  id: string;
  date: string;
  name: string;
  size: number;
  type: FileType;
  _brutContent: DocumentDto;
}

const typeLabel = (type: FileType) => 'files.documents.file-type.' + FileType[type].toLocaleLowerCase();

@Component({
  selector: 'cam-documents-grid',
  imports: [
    AgGridComponent,
    CommonModule,
    EmptyComponent,
    FilesDisplayComponent,
    FilesSlideComponent,
    GridControlComponent,
    GridTagComponent,
    LayoutFullPanelComponent,
  ],
  templateUrl: './documents-grid.component.html',
  styleUrls: ['./documents-grid.component.scss'],
})
export class DocumentsGridComponent extends CamBaseComponent implements OnInit {
  gridId = input.required<string>();

  documents$ = input.required<Observable<DocumentDto[]>>();

  compactView = input(false);

  defaultOpenId = model<string | null>(null);

  emptyText = input('');

  private _dataService = inject<CamGridDataService<DocumentEntity>>(CamGridDataService);

  @ViewChild('cardTemplate', { read: TemplateRef })
  cardTemplate!: TemplateRef<{ items: DocumentEntity[] }>;

  readonly data$ = signal<Observable<DocumentEntity[]> | null>(null);

  public fieldsOverrides: IFieldsOverrides = {
    date: { cellRenderer: GridDateCellComponent },
    name: { flex: 1 },
    size: {
      cellRenderer: SizeCellComponent,
      colOptions: {
        filters: {
          allow: false,
        },
      },
    },
    type: {
      colOptions: {
        enumInfo: {
          cellRenderer: FileTypeCellComponent,
          enum: FileType,
          values: extractEnum(FileType, true).map(value => ({
            id: value.value.toString(),
            name: typeLabel(value.value),
          })),
        },
      },
    },
  };

  public slideModal = new ModalState<{ files: DocumentDto[]; initial: DocumentDto | null }, null>();

  private _grid: CamGridData<DocumentEntity> | null = null;

  ngOnInit() {
    this.data$.set(
      this.documents$().pipe(
        map(list =>
          list.map<DocumentEntity>(item => ({
            id: item.id,
            date: item.uploadedDate,
            name: item.name ?? '',
            size: item.size ?? 0,
            type: item.fileType,
            _brutContent: item,
          }))
        )
      )
    );

    this._grid = this._dataService.get(this.gridId(), true);
    this._grid.switchView('grid');

    this._registerSubscription(
      this._grid.clickedRow$.subscribe(row => {
        this._openSlide(row._brutContent);
      })
    );

    if (this.defaultOpenId()) {
      this._registerSubscription(
        this._grid.filteredData$.subscribe(data => {
          if (!this.defaultOpenId()) {
            return;
          }

          const selected = data.find(elem => elem._brutContent.id === this.defaultOpenId());
          if (selected) {
            this._openSlide(selected._brutContent);
            this.defaultOpenId.set(null);
          }
        })
      );
    }
  }

  public idsFromItems(items: DocumentEntity[]): string[] {
    return items.map(i => i._brutContent.id);
  }

  private _openSlide(document: DocumentDto) {
    if (!this._grid) {
      return;
    }

    const files = this._grid.filteredData$.value?.map(e => e._brutContent) ?? [];
    this.slideModal.asked({ files, initial: document });
  }
}
